import { REBuilder } from './rebuilder.ts'

//

/**
 * Recognition options for schemaless links.
 *
 * @category types
 */
export interface LinkifyOptions {
  /** Recognize URLs without `http(s)://` prefix. Default `false`. */
  fuzzyLink?: boolean
  /** Recognize emails without `mailto:` prefix. Default `true`. */
  fuzzyEmail?: boolean
  /**
   * Allow IPs in fuzzy links. Can conflict with some texts, like version
   * numbers. Default `false`.
   */
  fuzzyIP?: boolean
  /**
   * Terminate link with `---` if it is considered a long dash. Default `false`.
   */
  '---'?: boolean
  /** Allowed TLDs list for fuzzy links. Replaces the default list when set. */
  tlds?: string[]
  /** Recognize authentication data in URLs. Default `false`. */
  urlAuth?: boolean
  /** Maximum link length. Default `10000`. */
  maxLength?: number
}

export interface LinkifyConstructorOptions extends LinkifyOptions {
  /** Custom regular expression builder. */
  rebuilder?: REBuilder
}

/**
 * Custom schema definition.
 *
 * @category types
 */
export interface SchemaOpts {
  /**
   * Checks text after the schema prefix. Should return matched tail length on
   * success, or `0` on fail.
   */
  validate: (text: string, pos: number, self: LinkifyIt) => number
  /**
   * Optional function to normalize `text` and `url` of matched result, for
   * example for `@twitter` mentions.
   */
  normalize?: (match: Match, self: LinkifyIt) => void
}

type Schema = Required<SchemaOpts>

interface MatchCandidate {
  schema: string
  index: number
  lastIndex: number
}

const web_schema: Schema = {
  validate: (text: string, pos: number, self: LinkifyIt) => {
    const re = self.re.get_http_validator()
    re.lastIndex = pos

    const m = re.exec(text)
    return m ? m[0].length : 0
  },
  normalize: (match: Match, self: LinkifyIt) => self.normalize(match)
}

const defaultSchemas: Record<string, Schema> = {
  'http:': web_schema,
  'https:': web_schema,
  'ftp:': web_schema,
  '//': {
    validate: function (text: string, pos: number, self: LinkifyIt) {
      const re = self.re.get_relative_proto_validator()
      re.lastIndex = pos

      const m = re.exec(text)
      if (m) {
        // should not be `://` & `///`, that protects from errors in protocol name
        if (pos >= 3 && text[pos - 3] === ':') { return 0 }
        if (pos >= 3 && text[pos - 3] === '/') { return 0 }
        return m[0].length
      }
      return 0
    },
    normalize: (match: Match, self: LinkifyIt) => self.normalize(match)
  },
  'mailto:': {
    validate: function (text: string, pos: number, self: LinkifyIt) {
      const re = self.re.get_mailto_validator()
      re.lastIndex = pos

      const m = re.exec(text)
      return m ? m[0].length : 0
    },
    normalize: (match: Match, self: LinkifyIt) => self.normalize(match)
  }
}

// List of 2-character tlds (autogenerated by ./support/tlds_2char_gen.js)
const tlds_2ch = 'a:cdefgilmnoqrstuwxz|b:abdefghijmnorstvwyz|c:acdfghiklmnoruvwxyz|d:ejkmoz|e:cegrstu|f:ijkmor|g:abdefghilmnpqrstuwy|h:kmnrtu|i:delmnoqrst|j:emop|k:eghimnprwyz|l:abcikrstuvy|m:acdeghklmnopqrstuvwxyz|n:acefgilopruz|o:m|p:aefghklmnrstwy|q:a|r:eosuw|s:abcdeghijklmnortuvxyz|t:cdfghjklmnortvwz|u:agksyz|v:aceginu|w:fs|y:et|z:amw'

// DON'T try to make PRs with changes. Extend TLDs with LinkifyIt.tlds() instead
const tlds_default = 'biz|com|edu|gov|net|org|pro|web|xxx|aero|asia|coop|info|museum|name|shop|рф'

function unpackTlds (): string[] {
  const result = tlds_default.split('|')

  tlds_2ch.split('|').forEach((item) => {
    const sep = item.indexOf(':')

    const prefix = item.slice(0, sep)

    for (const suffix of item.slice(sep + 1)) {
      result.push(prefix + suffix)
    }
  })

  return result
}

const defaultOptions: Required<LinkifyOptions> = {
  fuzzyLink: false,
  fuzzyEmail: true,
  fuzzyIP: false,
  '---': false,
  tlds: unpackTlds(),
  urlAuth: false,
  maxLength: 10000
}

/**
 * Match result returned by {@link LinkifyIt.match} and
 * {@link LinkifyIt.matchAtStart}.
 *
 * @category types
 */
export class Match {
  /** Prefix (protocol) for matched string. Empty for fuzzy links. */
  schema: string
  /** First position of matched string. */
  index: number
  /** Next position after matched string. */
  lastIndex: number
  /** Matched string. */
  raw: string
  /** Normalized text of matched string. */
  text: string
  /** Normalized URL of matched string. */
  url: string

  constructor (text: string, schema: string, index: number, lastIndex: number) {
    const raw = text.slice(index, lastIndex)

    this.schema = schema.toLowerCase()
    this.index = index
    this.lastIndex = lastIndex
    this.raw = raw
    this.text = raw
    this.url = raw
  }
}

/** Linkifier instance. */
export class LinkifyIt {
  __opts__: Required<LinkifyOptions>
  private __schemas__: Record<string, Schema>
  re: REBuilder

  /**
   * Creates new linkifier instance.
   *
   * By default understands:
   *
   * - `http(s)://...` , `ftp://...`, `mailto:...` & `//...` links
   * - "fuzzy" emails (foo@bar.com).
   *
   * See {@link LinkifyConstructorOptions} for available options.
   *
   * @param options Recognition options.
   *
   * @example
   * ```javascript
   * import { LinkifyIt } from 'linkify-it'
   *
   * const linkify = new LinkifyIt({ fuzzyLink: true })
   *
   * linkify
   *   .tlds(require('tlds'))       // Reload with full TLD list
   *   .tlds('onion', true)         // Add unofficial `.onion` domain
   *   .add('ftp:', null)           // Disable `ftp:` protocol
   *   .set({ fuzzyIP: true })      // Enable IPs in fuzzy links
   *
   * console.log(linkify.test('Site github.com!')) // true
   * console.log(linkify.match('Site github.com!'))
   * ```
   */
  constructor (options: LinkifyConstructorOptions = {}) {
    const { rebuilder, ...linkifyOptions } = options

    this.__opts__ = { ...defaultOptions, ...linkifyOptions }

    this.__schemas__ = { ...defaultSchemas }

    this.re = rebuilder || new REBuilder()
    this.re.set({
      ...this.__opts__,
      schema_names: Object.keys(this.__schemas__)
    })
  }

  /**
   * Add new rule definition.
   *
   * `schema` is a link prefix (usually, protocol name with `:` at the end,
   * `skype:` for example). `linkify-it` makes sure that prefix is not
   * preceded with alphanumeric char and symbols. Only whitespaces and
   * punctuation allowed.
   *
   * `definition` is a rule to check tail after link prefix. To disable an
   * existing rule, pass `null`.
   *
   * @param schema Rule name (fixed pattern prefix).
   * @param definition Schema definition, or `null` to disable the rule.
   *
   * See [twitter mentions example](https://github.com/markdown-it/linkify-it/blob/master/examples/twitter.mjs).
   */
  add (schema: string, definition: SchemaOpts | null = null): this {
    if (!definition) {
      delete this.__schemas__[schema]
    } else {
      const def = {
        normalize: (match: Match, self: LinkifyIt) => self.normalize(match),
        ...definition
      }
      this.__schemas__[schema] = def
    }

    this.re.set({
      ...this.__opts__,
      schema_names: Object.keys(this.__schemas__)
    })
    return this
  }

  /**
   * Set recognition options for links without schema.
   *
   * @param options Recognition options.
   */
  set (options: LinkifyOptions = {}): this {
    this.__opts__ = { ...this.__opts__, ...options }
    this.re.set({
      ...this.__opts__,
      schema_names: Object.keys(this.__schemas__)
    })
    return this
  }

  /**
   * Searches linkifiable pattern and returns `true` on success or `false` on fail.
   *
   * @param text Text to scan.
   */
  test (text: string): boolean {
    if (!text.length) { return false }

    let m, re

    // try to scan for link with schema - that's the most simple rule
    re = this.re.get_schema_search()
    re.lastIndex = 0
    while ((m = re.exec(text)) !== null) {
      if (this.testSchemaAt(text, m[2], re.lastIndex)) { return true }
    }

    if (this.__opts__.fuzzyLink && this.__schemas__['http:']) {
      // guess schemaless links
      re = this.re.get_fuzzy_link_search()
      re.lastIndex = 0
      if (re.exec(text) !== null) { return true }
    }

    if (this.__opts__.fuzzyEmail && this.__schemas__['mailto:']) {
      // guess schemaless emails
      if (text.indexOf('@') >= 0) {
        // We can't skip this check, because this cases are possible:
        // 192.168.1.1@gmail.com, my.in@example.com
        const mailHostRe = this.re.get_fuzzy_mail_host_search()
        const mailNameRe = this.re.get_mail_name_validator()
        mailHostRe.lastIndex = 0

        while ((m = mailHostRe.exec(text)) !== null) {
          const name = text.slice(Math.max(0, m.index - 65), m.index)
          if (mailNameRe.test(name)) { return true }
        }
      }
    }

    return false
  }

  /**
   * Similar to {@link LinkifyIt.test} but checks only specific protocol tail exactly
   * at given position. Returns length of found pattern (0 on fail).
   *
   * @param text Text to scan.
   * @param schema Rule (schema) name.
   * @param pos Text offset to check from.
   */
  testSchemaAt (text: string, schema: string, pos: number): number {
    // If not supported schema check requested - terminate
    if (!this.__schemas__[schema.toLowerCase()]) { return 0 }

    return this.__schemas__[schema.toLowerCase()].validate(
      text.slice(0, pos + this.__opts__.maxLength),
      pos,
      this
    )
  }

  /**
   * Returns array of found link descriptions or `null` on fail. We strongly
   * recommend to use {@link LinkifyIt.test} first, for best speed.
   *
   * @param text Text to scan.
   */
  match (text: string): Match[] | null {
    const result: Match[] = []
    const schemaRe = this.re.get_schema_search()
    let fuzzyLinkRe: RegExp | undefined
    let mailHostRe: RegExp | undefined
    let mailNameRe: RegExp | undefined
    let fuzzyLinkCandidate: MatchCandidate | undefined
    let fuzzyEmailCandidate: MatchCandidate | undefined
    let schemaPrefix: MatchCandidate | undefined
    let schemaDone = false
    let fuzzyLinkDone = false
    let fuzzyEmailDone = false
    let pos = 0

    if (!text.length) { return null }

    schemaRe.lastIndex = 0

    if (this.__opts__.fuzzyLink && this.__schemas__['http:']) {
      fuzzyLinkRe = this.re.get_fuzzy_link_search()
      fuzzyLinkRe.lastIndex = 0
    }

    if (this.__opts__.fuzzyEmail && this.__schemas__['mailto:']) {
      mailHostRe = this.re.get_fuzzy_mail_host_search()
      mailHostRe.lastIndex = 0
      mailNameRe = this.re.get_mail_name_validator()
    }

    for (;;) {
      const scanFrom = Math.max(pos - 1, 0)

      if (mailHostRe && mailNameRe && !fuzzyEmailDone && (!fuzzyEmailCandidate || fuzzyEmailCandidate.index < pos)) {
        if (mailHostRe.lastIndex < scanFrom) { mailHostRe.lastIndex = scanFrom }

        for (;;) {
          const m = mailHostRe.exec(text)
          if (!m) {
            fuzzyEmailDone = true
            fuzzyEmailCandidate = undefined
            break
          }

          const name = mailNameRe.exec(text.slice(Math.max(0, m.index - 65), m.index))
          if (!name) { continue }

          fuzzyEmailCandidate = {
            schema: 'mailto:',
            index: m.index - name[1].length,
            lastIndex: m.index + m[0].length
          }

          if (fuzzyEmailCandidate.index >= pos) { break }
          if (mailHostRe.lastIndex < scanFrom) { mailHostRe.lastIndex = scanFrom }
        }
      }

      if (fuzzyLinkRe && !fuzzyLinkDone && (!fuzzyLinkCandidate || fuzzyLinkCandidate.index < pos)) {
        if (fuzzyLinkRe.lastIndex < scanFrom) { fuzzyLinkRe.lastIndex = scanFrom }

        for (;;) {
          const m = fuzzyLinkRe.exec(text)
          if (!m) {
            fuzzyLinkDone = true
            fuzzyLinkCandidate = undefined
            break
          }

          fuzzyLinkCandidate = {
            schema: '',
            index: m.index + m[1].length,
            lastIndex: m.index + m[0].length
          }

          if (fuzzyLinkCandidate.index >= pos) { break }
          if (fuzzyLinkRe.lastIndex < scanFrom) { fuzzyLinkRe.lastIndex = scanFrom }
        }
      }

      let fuzzyCandidate = fuzzyEmailCandidate
      if (!fuzzyCandidate ||
        (fuzzyLinkCandidate &&
          (fuzzyLinkCandidate.index < fuzzyCandidate.index ||
            (fuzzyLinkCandidate.index === fuzzyCandidate.index && fuzzyLinkCandidate.lastIndex > fuzzyCandidate.lastIndex)))) {
        fuzzyCandidate = fuzzyLinkCandidate
      }

      let schemaCandidate: MatchCandidate | undefined

      if (!schemaDone) {
        for (;;) {
          if (!schemaPrefix) {
            if (schemaRe.lastIndex < scanFrom) { schemaRe.lastIndex = scanFrom }

            const m = schemaRe.exec(text)
            if (!m) {
              schemaDone = true
              break
            }

            schemaPrefix = {
              schema: m[2],
              index: m.index + m[1].length,
              lastIndex: m.index + m[0].length
            }
          }

          if (schemaPrefix.index < pos) {
            schemaPrefix = undefined
            continue
          }

          if (fuzzyCandidate && schemaPrefix.index > fuzzyCandidate.index) { break }

          const prefix = schemaPrefix
          schemaPrefix = undefined

          const len = this.testSchemaAt(text, prefix.schema, prefix.lastIndex)
          if (len) {
            schemaCandidate = {
              schema: prefix.schema,
              index: prefix.index,
              lastIndex: prefix.lastIndex + len
            }
            break
          }
        }
      }

      let candidate = schemaCandidate
      if (!candidate ||
        (fuzzyEmailCandidate &&
          (fuzzyEmailCandidate.index < candidate.index ||
            (fuzzyEmailCandidate.index === candidate.index && fuzzyEmailCandidate.lastIndex > candidate.lastIndex)))) {
        candidate = fuzzyEmailCandidate
      }
      if (!candidate ||
        (fuzzyLinkCandidate &&
          (fuzzyLinkCandidate.index < candidate.index ||
            (fuzzyLinkCandidate.index === candidate.index && fuzzyLinkCandidate.lastIndex > candidate.lastIndex)))) {
        candidate = fuzzyLinkCandidate
      }

      if (!candidate) { break }

      if (candidate === fuzzyEmailCandidate) {
        fuzzyEmailCandidate = undefined
      } else if (candidate === fuzzyLinkCandidate) {
        fuzzyLinkCandidate = undefined
      }

      const match = new Match(text, candidate.schema, candidate.index, candidate.lastIndex)
      if (match.schema) {
        this.__schemas__[match.schema].normalize(match, this)
      } else {
        this.normalize(match)
      }
      result.push(match)
      pos = candidate.lastIndex
    }

    if (result.length) {
      return result
    }

    return null
  }

  /**
   * Returns fully-formed (not fuzzy) link if it starts at the beginning
   * of the string, and null otherwise.
   *
   * @param text Text to scan.
   */
  matchAtStart (text: string): Match | null {
    if (!text.length) return null

    const m = this.re.get_schema_at_start().exec(text)
    if (!m) return null

    const len = this.testSchemaAt(text, m[2], m[0].length)
    if (!len) return null

    const match = new Match(text, m[2], m.index + m[1].length, m.index + m[0].length + len)

    this.__schemas__[match.schema].normalize(match, this)
    return match
  }

  /**
   * Load (or merge) new TLDs list. Those are used for fuzzy links (without
   * prefix) to avoid false positives. By default this algorithm is used:
   *
   * - hostname with any 2-letter root zones are ok.
   * - biz|com|edu|gov|net|org|pro|web|xxx|aero|asia|coop|info|museum|name|shop|рф
   *   are ok.
   * - encoded (`xn--...`) root zones are ok.
   *
   * If list is replaced, then exact match for 2-chars root zones will be checked.
   *
   * @param list List of TLDs.
   * @param keepOld Merge with current list if `true` (`false` by default).
   */
  tlds (list: string | string[], keepOld = false): this {
    list = Array.isArray(list) ? list : [list]

    if (!keepOld) {
      this.__opts__.tlds = list
    } else {
      this.__opts__.tlds = this.__opts__.tlds.concat(list)
    }

    this.re.set({
      ...this.__opts__,
      schema_names: Object.keys(this.__schemas__)
    })
    return this
  }

  /**
   * Default normalizer (if schema does not define its own).
   *
   * @param match Match to normalize.
   */
  normalize (match: Match): void {
    // Do minimal possible changes by default. Need to collect feedback prior
    // to move forward https://github.com/markdown-it/linkify-it/issues/1

    if (!match.schema) { match.url = `http://${match.url}` }

    if (match.schema === 'mailto:' && !/^mailto:/i.test(match.url)) {
      match.url = `mailto:${match.url}`
    }
  }
}

function linkifyit (options: LinkifyConstructorOptions = {}): LinkifyIt {
  return new LinkifyIt(options)
}

export { linkifyit }
